<template>
  <div>
    <app-navbar class="navbar-light border-bottom px-0" v-if="shouldShowToolbar()">
      <strong class="navbar-text d-none d-md-inline-block" v-if="hasFilters()">
        Filter:
      </strong>
      <ul class="navbar-nav mr-auto" v-if="hasFilters()">
        <li
          :class="{ 'active': activeFilterName === '' }"
          class="nav-item">
          <a href="#" class="nav-link" @click.prevent="applyFilter('')">All</a>
        </li>
        <li
          v-for="(filter, key) in filters"
          :key="key"
          :class="{ 'active': key === activeFilterName }"
          class="nav-item">
          <a href="#" class="nav-link" @click.prevent="applyFilter(key)">{{ filter.label }}</a>
        </li>
      </ul>
      <form class="form-inline my-2 my-lg-0 ml-auto" @submit.prevent="doSearch">
        <base-field v-if="searchable" row>
          <base-input v-model="search" disable-smart-placeholder placeholder="Search" />
          <template slot="addonRight">
            <base-button icon-right="search" submit>
              Search
            </base-button>
          </template>
        </base-field>
        <base-button
          v-if="refresh"
          @click="request"
          class="ml-2"
          icon-right="sync-alt">
          Refresh
        </base-button>
      </form>
    </app-navbar>
    <slot name="top"></slot>
    <template v-if="loaded && totalPages">
      <base-table>
        <thead>
          <tr>
            <template v-for="(header, key) in headers">
              <th v-if="shouldShowColumn(key)" :key="key">
                {{ header }}
              </th>
            </template>
            <th v-if="hasActions()">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, key) in rows" :key="key">
            <template v-for="(column, index) in Object.keys(columns)">
              <td v-if="shouldShowColumn(column)" :key="index">
                <template v-if="columnIsIcon(column)">
                  <base-icon v-bind="columnIcon(column, row)"/>
                </template>
                <template v-else>
                  <div v-html="cell(column, row)"></div>
                </template>
              </td>
            </template>
            <td v-if="hasActions()">
              <div class="btn-group">
                <template v-for="(action, index) in availableActions(row)">
                  <base-button
                    v-bind="actionProps(action, row)"
                    :key="index"
                    secondary
                    small
                    @click="actionClick(action, row)" />
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </base-table>
      <base-pagination
        :current="page"
        :total="totalPages"
        class="mt-4"
        @go="goToPage" />
    </template>
    <template v-if="loaded && !totalPages">
      <p class="text-center" :class="{ 'mt-3': hasFilters() }">{{ noResultsMessage }}</p>
      <slot name="none"></slot>
    </template>
    <template v-if="!loaded">
      <base-loader>
        {{ loadingMessage }}
      </base-loader>
    </template>
    <slot name="bottom"></slot>
  </div>
</template>

<script>
import { EventBus } from '@/libs'

import AppNavbar from './AppNavbar.vue'
import BasePagination from './BasePagination.vue'

export default {
  mounted () {
    if (this.$_.isString(this.filter)) {
      this.applyFilter(this.filter, false)
    }

    this.request()

    EventBus.$on('reload-PaginatedList', this.request)
    EventBus.$on(`reload-PaginatedList-${this.service}`, this.request)
  },

  data () {
    return {
      activeFilter: null,
      activeFilterName: '',
      autoSearchDelay: null,
      loaded: false,
      page: 1,
      search: ''
    }
  },

  name: 'PaginatedList',

  components: {
    AppNavbar,
    BasePagination
  },

  props: {
    actions: Array,
    allFilter: Boolean,
    autoSearchDelayTime: { default: 300, type: Number },
    columns: { required: true, type: Object },
    dispatch: { default: 'list', type: String },
    filter: String,
    filters: Object,
    heading: String,
    loadingMessage: { default: '', type: String },
    noResultsMessage: { default: 'No results found.', type: String },
    pageSize: { default: 20, type: Number },
    populate: { type: Array },
    query: Object,
    refresh: { default: false, type: Boolean },
    searchable: Boolean,
    stateKey: { default: '', type: String },
    service: { required: true, type: String },
    sort: { default: '-createdAt', type: String }
  },

  computed: {
    headers () {
      const headers = []
      const add = header => headers.push(header)

      this.$_.forIn(this.columns, (column, key) => {
        if (this.$_.isObject(column) && this.$_.has(column, 'header') && this.shouldShowColumn(key)) {
          add(column.header)
        } else if (this.$_.isString(column)) {
          add(column)
        }
      })

      return headers
    },

    rows () {
      return this.state.rows
    },

    state () {
      return this.$_.get(this.$store.state.core, this.stateKey || this.service)
    },

    totalPages () {
      return this.state.totalPages
    }
  },

  methods: {
    actionClick (action, row) {
      if (this.$_.has(action, 'click') && this.$_.isFunction(action.click)) {
        action.click(row)
      }
    },

    actionProps (action, row) {
      const props = this.$_.cloneDeep(action)

      if (this.$_.has(props, 'to')) {
        if (this.$_.isFunction(props.to)) {
          props.to = props.to(row)
        }

        const params = props.to.params || {}
        const attribute = this.$_.get(props, 'id') || '_id'

        props.to.params = this.$_.merge(params, { id: this.$_.get(row, attribute) })
      }

      return props
    },

    applyFilter (filter, request = true) {
      this.activeFilter = this.$_.get(this.filters, filter)
      this.activeFilterName = filter

      if (request) this.request()
    },

    autoSearch () {
      if (this.autoSearchDelay) {
        clearTimeout(this.autoSearchDelay)
      }

      this.autoSearchDelay = setTimeout(this.doSearch, this.autoSearchDelayTime)
    },

    availableActions (row) {
      const actions = []

      this.$_.forIn(this.actions, action => {
        const conditional = this.$_.has(action, 'showIf')

        if (!conditional || action.showIf(row)) {
          actions.push(action)
        }
      })

      return actions
    },

    buildQuery () {
      let query = {}

      if (this.$_.isPlainObject(this.activeFilter) && this.$_.isPlainObject(this.activeFilter.query)) {
        this.$_.forIn(this.activeFilter.query, (v, k) => {
          query[k] = v
        })
      }

      if (this.$_.isPlainObject(this.query)) {
        this.$_.forIn(this.query, (v, k) => {
          query[k] = v
        })
      }

      return { query }
    },

    cell (key, row) {
      const column = this.$_.get(this.columns, key)
      let content = this.$_.get(row, key)

      if (this.$_.isObject(column)) {
        if (this.$_.has(column, 'format') && this.$_.isFunction(column.format)) {
          content = column.format(row)
        }
      }

      return content
    },

    columnIcon (key, row) {
      const icons = this.$_.get(this.columns[key], 'icons')
      const value = this.$_.get(row, key)
      const icon = this.$_.get(icons, value)

      if (this.$_.isString(icon)) {
        return { icon }
      }

      return icon
    },

    doSearch () {
      this.page = 1
      this.request()
    },

    hasActions () {
      return this.actions && this.actions.length !== 0
    },

    hasFilters () {
      return this.filters && Object.keys(this.filters).length !== 0
    },

    goToPage (page) {
      this.page = page
      this.request()
    },

    async request () {
      const {
        dispatch,
        // filter,
        page,
        pageSize,
        populate,
        // search,
        service,
        sort
      } = this
      const query = this.buildQuery()

      await this.$store.dispatch(`core/${service}/${dispatch}`, {
        page,
        pageSize,
        populate,
        sort,
        ...query
      })

      this.loaded = true
    },

    columnIsIcon (column) {
      return this.$_.has(this.columns[column], 'icons')
    },

    shouldShowColumn (key) {
      const column = this.$_.get(this.columns, key)

      if (!this.$_.isObject(column)) return true

      const conditional = this.$_.has(column, 'showIf')
      const { dispatch, filter, where } = this
      const args = { dispatch, filter, where }

      if (!conditional || (conditional && column.showIf(args) === true)) {
        return true
      }

      return false
    },

    shouldShowToolbar () {
      return this.hasFilters() || this.refresh || this.searchable
    }
  },

  watch: {
    where () {
      this.request()
    }
  }
}
</script>
